import {
  currencySchema,
  currencyUI,
  descriptionUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { FULL_SCHEMA } from '../utils/imports';
import { subtitleUI } from './titles';

const getFieldSchema = ({ dataField, arrayPath }) =>
  arrayPath
    ? FULL_SCHEMA.properties[arrayPath].items.properties[dataField]
    : FULL_SCHEMA.properties[dataField];

const createGroupedCurrencyUI = ({
  dataField,
  label,
  subtitle = null,
  description = null,
  arrayPath = null,
}) => {
  const fieldSchema = getFieldSchema({ dataField, arrayPath });
  return {
    ...(subtitle && subtitleUI(subtitle)),
    ...(description && descriptionUI(description)),
    [dataField]: currencyUI({
      title: label,
      min: fieldSchema.minimum,
      max: fieldSchema.maximum,
    }),
  };
};

const createGroupedCurrencySchema = ({ dataField }) => ({
  type: 'object',
  required: [dataField],
  properties: {
    [dataField]: { ...currencySchema },
  },
});

/**
 * Builds uiSchema properties for grouped currency fields.
 *
 * @param {Object[]} config
 * @param {string} config[].dataField Field name used to generate the view wrapper key.
 * @param {string} config[].label Label shown for the currency input.
 * @param {string} [config[].subtitle] Optional subtitle shown above the input.
 * @param {(string|Function|React.Component)} [config[].description] Optional description content.
 * @param {Object} [options]
 * @param {string} [options.arrayPath] Optional schema array path used to resolve min and max values.
 * @returns {Object} uiSchema properties keyed by view:<dataField>.
 */
export const buildGroupedCurrencyUIProperties = (config, { arrayPath } = {}) =>
  config.reduce((acc, definition) => {
    const viewField = `view:${definition.dataField}`;
    return {
      ...acc,
      [viewField]: createGroupedCurrencyUI({ ...definition, arrayPath }),
    };
  }, {});

/**
 * Builds schema properties for grouped currency fields.
 *
 * @param {Object[]} config
 * @param {string} config[].dataField Field name used to generate the view wrapper key.
 * @returns {Object} Schema properties keyed by view:<dataField>.
 */
export const buildGroupedCurrencySchemaProperties = config =>
  config.reduce((acc, definition) => {
    const viewField = `view:${definition.dataField}`;
    return { ...acc, [viewField]: createGroupedCurrencySchema(definition) };
  }, {});
